import { Text } from "@dropbox/dig-components/typography";
import { typeahead } from "@mirage/service-typeahead-search/service/types";
import i18n from "@mirage/translations";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { getCustomResultRow } from "../getCustomResultRow";
import styles from "./OmniboxResultRowAdapter.module.css";
import { ResultRowWrapper } from "./ResultRowWrapper";

import type { Props as TypeaheadResultRowProps } from "@mirage/search/SearchResults/TypeaheadResultRow";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import classNames from "classnames";
import type { MouseEvent } from "react";
import type { OmniboxTypeaheadResult } from "./useConvertToOmniboxResults";

type MaybeLiEl = HTMLLIElement | null;

export const OmniboxResultRowAdapter = forwardRef<
  HTMLLIElement,
  Omit<TypeaheadResultRowProps, "result"> & {
    filters: SearchFilter[];
    isFirstInSection: boolean;
    result: OmniboxTypeaheadResult;
  }
>(
  (
    {
      result,
      query,
      filters,
      isSelected = false,
      // disabled = false, // NOTE: not supporting disable results (subtle text)
      isFocused = false,
      setIsHovered,
      setIsUnhovered,
      onClick,
      observer,
      resultRowIndex,
      isMouseActive = true,
      isFirstInSection = false,
    },
    ref,
  ) => {
    const [wrapperEl, setWrapperEl] = useState<MaybeLiEl>(null);
    useImperativeHandle<MaybeLiEl, MaybeLiEl>(ref, () => wrapperEl);

    // Observe and unobserve this paragraph
    useEffect(() => {
      if (observer) {
        if (wrapperEl) {
          observer.observe(wrapperEl);
        }
        return () => {
          if (wrapperEl) {
            observer.unobserve(wrapperEl);
          }
        };
      }
    }, [observer, wrapperEl]);

    const handleClick = (e: MouseEvent<HTMLLIElement>) => {
      onClick?.(e);
    };

    // NOTE: Leaving this for Alan to adopt into a finalized implementation
    const renderSectionHeading = () => {
      const SECTION_MAP: Record<typeahead.SectionId, string> = {
        [typeahead.SectionId.Find]: i18n.t("omnibox_section_find"),
        [typeahead.SectionId.Recommended]: i18n.t(
          "omnibox_section_recommended",
        ),
        [typeahead.SectionId.Recent]: i18n.t("omnibox_section_recent"),
        [typeahead.SectionId.AskDash]: i18n.t("omnibox_section_ask_dash"),
        [typeahead.SectionId.Ask]: i18n.t("omnibox_section_ask"),
        [typeahead.SectionId.Organize]: i18n.t("omnibox_section_organize"),
        [typeahead.SectionId.Create]: i18n.t("omnibox_section_create"),
        [typeahead.SectionId.Suggested]: i18n.t("omnibox_section_suggested"),
        [typeahead.SectionId.Draft]: i18n.t("omnibox_section_draft"),
        [typeahead.SectionId.DraftFromTemplate]: i18n.t(
          "omnibox_section_draft_from_template",
        ),
        [typeahead.SectionId.DraftFromTone]: i18n.t(
          "omnibox_section_draft_from_tone",
        ),
        [typeahead.SectionId.DraftFromSource]: i18n.t(
          "omnibox_section_draft_from_source",
        ),
      };

      return (
        <li
          className={classNames(styles.sectionHeader, {
            [styles.isTopSection]: resultRowIndex === 0,
          })}
          aria-hidden={true}
        >
          <Text size={"small"} isBold={true} color={"subtle"}>
            {SECTION_MAP[result.scoredResult.sectionId!]}
          </Text>
        </li>
      );
    };

    return (
      <>
        {isFirstInSection &&
          result.scoredResult.sectionId &&
          renderSectionHeading()}
        <ResultRowWrapper
          isSelected={isSelected}
          isFocused={isFocused}
          isMouseActive={isMouseActive}
          resultRowIndex={resultRowIndex}
          onClick={handleClick}
          setIsHovered={setIsHovered}
          setIsUnhovered={setIsUnhovered}
          uuid={result.uuid}
          ref={(el: HTMLLIElement | null) => {
            setWrapperEl(el);
            if (typeof ref === "function") {
              ref(el);
            } else if (ref) {
              ref.current = el;
            }
          }}
        >
          {getCustomResultRow({
            filters,
            resultType: result.type,
            scoredResult: result.scoredResult,
            isSelected: isSelected || isFocused,
            currentQuery: query,
          })}
        </ResultRowWrapper>
      </>
    );
  },
);
OmniboxResultRowAdapter.displayName = "OmniboxResultRowAdapter";
